import { Link2, Loader2, RefreshCw, Webhook } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { bs } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  useStoreSyncStatus,
  useEnableSync,
  useDisableSync,
  useResyncStore,
  useRefreshWebhooks,
  useFullResyncStore,
} from "@/hooks/use-stores";

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  try {
    return formatDistanceToNow(d, { addSuffix: true, locale: bs });
  } catch {
    return d.toLocaleString("bs-BA");
  }
}

export function WebhookSyncSection({ storeId }: { storeId: string }) {
  const { data: status, isLoading } = useStoreSyncStatus(storeId);
  const enableSync = useEnableSync();
  const disableSync = useDisableSync();
  const resyncStore = useResyncStore(storeId);
  const refreshWebhooks = useRefreshWebhooks(storeId);
  const fullResync = useFullResyncStore(storeId);

  async function handleEnable() {
    try {
      await enableSync.mutateAsync(storeId);
      toast.success("Webhook mod uključen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri uključivanju webhook moda");
    }
  }

  async function handleDisable() {
    try {
      await disableSync.mutateAsync(storeId);
      toast.success("Webhook mod isključen");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri isključivanju webhook moda");
    }
  }

  async function handleResync() {
    try {
      await resyncStore.mutateAsync();
      toast.success("Sinhronizacija pokrenuta");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri sinhronizaciji");
    }
  }

  async function handleRefreshWebhooks() {
    try {
      const r = await refreshWebhooks.mutateAsync(undefined);
      const parts = [`osvježeno ${r.updated}`];
      if (r.created) parts.push(`dodano ${r.created}`);
      if (r.recreated) parts.push(`ponovo kreirano ${r.recreated}`);
      if (r.failed.length) {
        toast.warning(`Webhookovi: ${parts.join(", ")} — nije uspjelo: ${r.failed.map((f) => f.topic).join(", ")}`);
      } else {
        toast.success(`Webhookovi pokazuju na ${r.deliveryUrl} (${parts.join(", ")})`);
      }
    } catch (e: any) {
      toast.error(e?.message || "Greška pri osvježavanju webhookova");
    }
  }

  async function handleFullResync() {
    try {
      const r = await fullResync.mutateAsync();
      toast.success(
        `Puna sinhronizacija gotova — kupci: ${r.customers}, uklonjeno: ${r.deletedOrders} narudžbi, ${r.deletedProducts} proizvoda, ${r.deletedCustomers} kupaca`,
      );
    } catch (e: any) {
      toast.error(e?.message || "Greška pri punoj sinhronizaciji");
    }
  }

  const busy =
    enableSync.isPending ||
    disableSync.isPending ||
    resyncStore.isPending ||
    refreshWebhooks.isPending ||
    fullResync.isPending;
  const backfillRunning = status?.backfillStatus === "running";
  const backfillError = status?.backfillStatus === "error";

  return (
    <div className="ring-1 ring-foreground/10">
      <div className="border-b bg-muted/50 px-4 py-2 flex items-center gap-1.5">
        <Webhook className="h-3.5 w-3.5" />
        <span className="text-xs font-medium">Webhook sinkronizacija</span>
      </div>
      <div className="p-3 flex flex-col gap-2 text-xs">
        {isLoading || !status ? (
          <span className="text-muted-foreground">Učitavanje…</span>
        ) : (
          <>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Način rada</span>
              <span className="font-medium">
                {status.syncMode === "webhook" ? "Webhook (lokalni podaci)" : "API"}
              </span>
            </div>

            {status.syncMode === "webhook" && (
              <>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Zadnja sinhronizacija</span>
                  <span>{formatTime(status.lastSyncedAt)}</span>
                </div>
                <div className="text-muted-foreground">
                  Narudžbe: {status.counts.orders} · Proizvodi: {status.counts.products} · Kupci: {status.counts.customers}
                </div>
                {status.failedEvents > 0 && (
                  <span className="text-red-400">Zaglavljeni eventi: {status.failedEvents}</span>
                )}
              </>
            )}

            {backfillRunning && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>Backfill u toku…</span>
              </div>
            )}

            {backfillError && (
              <div className="flex flex-col gap-1.5">
                <span className="text-red-400">Backfill nije uspio — pokušaj ponovo</span>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleEnable}
                  disabled={busy}
                  className="self-start"
                >
                  {enableSync.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3 w-3" />
                  )}
                  Pokušaj ponovo
                </Button>
              </div>
            )}

            {status.syncMode === "api" && !backfillError && (
              <Button
                variant="outline"
                size="xs"
                onClick={handleEnable}
                disabled={busy || backfillRunning}
                className="self-start"
              >
                {enableSync.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                Uključi webhook mod
              </Button>
            )}

            {status.syncMode === "webhook" && (
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleDisable}
                  disabled={busy}
                  className="self-start"
                >
                  {disableSync.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                  Isključi webhook mod
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleResync}
                  disabled={busy}
                  className="self-start"
                >
                  {resyncStore.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Sinhronizujem…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3" />
                      Resync sad
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleFullResync}
                  disabled={busy}
                  className="self-start"
                  title="Pun prolaz: povlači kupce i uklanja obrisane narudžbe/proizvode iz lokalne kopije"
                >
                  {fullResync.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Sinhronizujem…
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3" />
                      Pun resync
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={handleRefreshWebhooks}
                  disabled={busy}
                  className="self-start"
                  title="Prepisuje adresu za isporuku webhookova na trenutnu domenu — pokreni nakon promjene domene"
                >
                  {refreshWebhooks.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Osvježavam…
                    </>
                  ) : (
                    <>
                      <Link2 className="h-3 w-3" />
                      Osvježi webhook adrese
                    </>
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
